import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Stack,
  Popover,
  Grid,
  Menu,
  Collapse,
  Divider,
  Autocomplete,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import { Edit, Delete as DeleteIcon, Visibility } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import { getAllItems, deleteItem, getCategories, fetchSubCategories, getSizes, ItemListExport } from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useMediaQuery, useTheme } from "@mui/material";
import { debounce } from "lodash";
import { useLocation } from "react-router-dom";
import { Description as FileIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import { motion } from "framer-motion";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import LayersIcon from "@mui/icons-material/Layers";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

export default function ItemTable() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [stats, setStats] = useState([]);

  const cardColors = ["#9b59b6", "#3498db", "#7ed321", "#c4b5f6"];

  const fetchData = async () => {
    setStats(
      [
        {
          label: "Total Category",
          icon: <CategoryIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        },
        {
          label: "Unit",
          icon: <LayersIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        },
        {
          label: "Items",
          icon: <Inventory2Icon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        },
      ].map((stat, i) => ({
        ...stat,
        bgColor: cardColors[i % cardColors.length],
      }))
    );
  };

  const [filters, setFilters] = useState({
    item_code: "",
    name: "",
    store_item_type: "",
    category: null,
    sub_category: null,
    size: null,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedPO, setExpandedPO] = useState(null);

  const isAnyFilterApplied = () => {
    return (
      filters.category !== null ||
      filters.sub_category !== null ||
      filters.size !== null ||
      filters.name.trim() !== "" ||
      filters.item_code.trim() !== ""
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchItems(1, false);
    }, 1000);

    return () => clearTimeout(handler);
  }, [filters]);

  const fetchItems = async (page = 1, append = false) => {
    const effectiveDateRange = isAnyFilterApplied() ? "all" : "today";
    const apiFilters = {
      item_code: filters.item_code,
      name: filters.name,
      store_item_type: filters.store_item_type,
      category: filters.category?.name || "",
      sub_category: filters.sub_category?.name || "",
      item_size: filters.size?.id || "",
      date_range: effectiveDateRange,
    };
    const data = await getAllItems(page, apiFilters);

    if (append) {
      setItems((prev) => [...prev, ...data.results]);
    } else {
      setItems(data.results);
    }

    setHasMore(!!data.next);
  };

  const fetchMoreData = () => {
    if (hasMore) {
      fetchItems(page + 1, true);
      setPage((prev) => prev + 1);
    }
  };

  const location = useLocation();

  useEffect(() => {
    if (location.state?.edited) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleDelete = async (id) => {
    try {
      await deleteItem(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  function formatPrice(price) {
    if (!price && price !== 0) return "N/A";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  useEffect(() => {
    fetchCategoriesData();
    fetchSizesData();
  }, []);

  const fetchCategoriesData = async (search = "") => {
    try {
      const data = await getCategories(1, search);
      setCategories(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {}
  };

  const fetchSubcategoriesData = async (categoryId, search = "") => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    try {
      const data = await fetchSubCategories(categoryId, 1, search);
      setSubcategories(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      setSubcategories([]);
    }
  };

  const fetchSizesData = async (search = "") => {
    try {
      const data = await getSizes(search);
      setSizes(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

  const handleCategorySearch = debounce((searchValue) => {
    fetchCategoriesData(searchValue);
  }, 500);

  const handleSizeSearch = debounce((searchValue) => {
    fetchSizesData(searchValue);
  }, 500);

  // ✅ Excel Export Handler - matches both desktop and mobile view
  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const effectiveDateRange = isAnyFilterApplied() ? "all" : "today";
      const apiFilters = {
        item_code: filters.item_code || "",
        name: filters.name || "",
        category_name: filters.category?.name || "",
        sub_category: filters.sub_category?.name || "",
        item_size: filters.size?.size || "",
        date_range: effectiveDateRange,
      };

      const blob = await ItemListExport(apiFilters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Item_List_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);

  return (
    <>
      {/* Filters & Add Item Button */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{
          mt: 0,
          pt: 0,
          mb: 1,
          width: "100%",
          gap: 2,
        }}
      >
        <ToastContainer position="top-right" autoClose={3000} />

        <Autocomplete
          options={categories}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          value={filters.category}
          onChange={(e, newValue) => {
            setFilters({
              ...filters,
              category: newValue,
              sub_category: null,
            });
            if (newValue) {
              fetchSubcategoriesData(newValue.id);
            } else {
              setSubcategories([]);
            }
          }}
          onInputChange={(e, newValue, reason) => {
            if (reason === "input") {
              handleCategorySearch(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              size="small"
              sx={{
                width: { xs: "100%", sm: "225px" },
                "& .MuiInputBase-root": {
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: "0px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          )}
        />

        <Autocomplete
          options={sizes}
          getOptionLabel={(option) => option?.size || ""}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          value={filters.size}
          onChange={(e, newValue) => {
            setFilters({ ...filters, size: newValue });
          }}
          onInputChange={(e, newValue, reason) => {
            if (reason === "input") {
              handleSizeSearch(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product Type"
              size="small"
              sx={{
                width: { xs: "100%", sm: "225px" },
                "& .MuiInputBase-root": {
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: "0px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          )}
        />

        <TextField
          label="Item Name"
          value={filters.name}
          onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          size="small"
          sx={{
            width: { xs: "100%", sm: "225px" },
            "& .MuiInputBase-root": {
              height: 30,
              fontSize: "0.7rem",
              borderRadius: "0px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem",
            },
          }}
        />

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          ml={{ xs: 0, sm: "auto" }}
          width={{ xs: "100%", sm: "auto" }}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            fullWidth={{ xs: true, sm: false }}
            onClick={() =>
              navigate("/dashboard/item-form", {
                state: { mode: "create" },
              })
            }
            sx={{
              height: 30,
              fontSize: "0.7rem",
              padding: "2px 15px",
              borderRadius: "0px",
            }}
          >
            Item
          </Button>
        </Box>

        <Tooltip title={excelLoading ? "Exporting..." : "Download Excel"} arrow>
          <span>
            <IconButton
              color="success"
              size="small"
              onClick={handleExcelExport}
              disabled={excelLoading}
              sx={{
                borderRadius: "4px",
                padding: "6px",
                border: "1px solid #4caf50",
                "&:hover": {
                  backgroundColor: "#e8f5e9",
                },
              }}
            >
              {excelLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

     

      <InfiniteScroll
        dataLength={items.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={<h4>Loading...</h4>}
        endMessage={
          <Typography sx={{ textAlign: "center", py: 2, color: "#999" }}>
            No more items!
          </Typography>
        }
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW - Now matches Desktop Table */
          <Box sx={{ bgcolor: "background.paper" }}>
            {items.map((item, index) => (
              <Box key={item.id || index}>
                {/* Main Row - Always Visible */}
                <Box
                  onClick={() =>
                    setExpandedItem(expandedItem === item.id ? null : item.id)
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:active": {
                      bgcolor: "#e3f2fd",
                    },
                    borderLeft: "4px solid #1976d2",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                        {index + 1}
                      </Typography>
                      <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                        {item.item_code}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.7rem", color: "#666", ml: 4 }}>
                      {item.name?.substring(0, 30) || "N/A"}
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedItem === item.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                </Box>

                {/* Expanded Details - Now matches Desktop Table */}
                <Collapse in={expandedItem === item.id} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 1.5, py: 1, bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa", borderLeft: "4px solid #1976d2" }}>
                    {/* 2 Column Grid Layout */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 1 }}>
                      {/* <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          ITEM CODE
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.item_code}
                        </Typography>
                      </Box> */}
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          PRODUCT TYPE
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.size_name || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          CATEGORY
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.category_name || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          CURRENT STOCK
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.current_stock || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          MIN QTY
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.min_quantity || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          MAX QTY
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.max_quantity || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          PRICE
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {formatPrice(item.price)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          TOTAL VALUE
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {formatPrice(item.total_value)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          CREATED BY
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.created_by_name || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          CREATED AT
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {formatOnlyDate(item.created_at)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Item Name - Full Width */}
                    <Box sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                        ITEM NAME
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3, fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", fontWeight: 600, letterSpacing: "0.5px" }}>
                        ACTIONS
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.6 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/dashboard/item-form", {
                              state: { mode: "edit", data: item },
                            });
                          }}
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#bbdefb",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Edit sx={{ fontSize: "1rem", color: "#1976d2" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setItemToDelete(item.id);
                          }}
                          sx={{
                            bgcolor: "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#ffcdd2",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: "1rem", color: "#d32f2f" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
                <Divider />
              </Box>
            ))}
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - Table */
          <TableContainer
            component={Paper}
            id="item-table"
            sx={{
              maxHeight: "88vh",
              overflowX: "auto",
              overflowY: "auto",
              width: "100%",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1270,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead sx={{
                "& th": {
                  fontWeight: "bold",
                  fontsize: "0.75rem",
                  color: (theme) => theme.palette.mode === "dark" ? "#2196f3" : "#333",
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                },
              }}>
                <TableRow>
                  <TableCell sx={{ position: "sticky", left: 0, backgroundColor: "#f5f5f5", zIndex: 3, minWidth: 50 }}>
                    S. No.
                  </TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Product Type</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">Min Qty</TableCell>
                  <TableCell align="right">Max Qty</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell align="right">Created by</TableCell>
                  <TableCell align="right">Created at</TableCell>
                  <TableCell align="center" sx={{ position: "sticky", right: 0, backgroundColor: "#f5f5f5", zIndex: 3, minWidth: 80 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((item, index) => (
                  <TableRow
                    hover
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": { backgroundColor: "#e3f2fd !important" },
                      },
                      "& td": {
                        paddingRight: "13px !important",
                        paddingLeft: "13px !important",
                        padding: "0px",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell align="center" sx={{ position: "sticky", left: 0, backgroundColor: "#fafafa", zIndex: 2, minWidth: 50 }}>
                      {index + 1}
                    </TableCell>
                   <TableCell>
  {item.name.match(/.{1,70}(\s|$)/g)?.map((line, index) => (
    <div key={index}>{line}</div>
  ))}
</TableCell>
                    <TableCell>{item.category_name}</TableCell>
                    <TableCell>{item.size_name}</TableCell>
                    <TableCell align="right">{item.current_stock}</TableCell>
                    <TableCell align="right">{item.min_quantity}</TableCell>
                    <TableCell align="right">{item.max_quantity}</TableCell>
                    <TableCell align="right">{formatPrice(item.price)}</TableCell>
                    <TableCell align="right">{formatPrice(item.total_value)}</TableCell>
                    <TableCell align="right">{item.created_by_name}</TableCell>
                    <TableCell align="right">{formatOnlyDate(item.created_at)}</TableCell>
                    <TableCell align="center" className="no-print" sx={{ position: "sticky", right: 0, backgroundColor: "#fafafa", zIndex: 2, minWidth: 80 }}>
                      <IconButton sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }} size="small" color="primary" onClick={() =>
                        navigate("/dashboard/item-form", {
                          state: { mode: "edit", data: item },
                        })
                      }>
                        <Edit transform="scale(0.6)" />
                      </IconButton>
                      <IconButton sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }} size="small" color="error" onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setItemToDelete(item.id);
                      }}>
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setItemToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box display="flex" justifyContent="flex-end" mt={1} gap={1}>
            <Button color="error" size="small" onClick={async () => {
              await handleDelete(itemToDelete);
              setAnchorEl(null);
              setItemToDelete(null);
            }}>
              Yes
            </Button>
            <Button size="small" onClick={() => setAnchorEl(null)}>
              No
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}